"use client";

import React from 'react';
import { SITE_NAME, TELEGRAM_URL } from '@/config';
import { InfoCircleIcon } from '@/components/ui/Icons';
import { useRedirect } from '@/context/RedirectContext';

const Highlight: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <span className="font-semibold text-orange-400">{children}</span>
);

export default function AboutClient() {
    const { redirect } = useRedirect();

    return (
        <>
            <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl sm:rounded-2xl bg-orange-500/20 flex items-center justify-center border border-orange-500/30 flex-shrink-0">
                    <InfoCircleIcon className="text-xl sm:text-2xl text-orange-400" />
                </div>
                <div>
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-white">About {SITE_NAME}</h1>
                    <p className="text-xs sm:text-sm text-slate-400 mt-1">Discover our mission and service standard</p>
                </div>
            </div>

            <div className="space-y-6 text-slate-300 text-base leading-relaxed">
                <p><Highlight>{SITE_NAME}</Highlight> is your premier destination for accessing a vast library of high-quality entertainment content. Our platform is designed with user experience in mind, offering a seamless and intuitive interface that makes discovering your next favorite movie or TV show effortless and enjoyable.</p>
                <p>Our mission is simple: to provide a reliable and comprehensive source for entertainment that is accessible to everyone. We believe that quality content should be easy to find and enjoy, which is why we've built a platform that prioritizes both an extensive collection and a user-friendly design.</p>

                <div>
                    <h2 className="font-bold text-xl sm:text-2xl text-white pt-6 pb-2 border-b border-white/5 mb-4">
                        What Sets Us Apart?
                    </h2>
                    <ul className="list-none space-y-4">
                        <li className="flex items-start gap-3">
                            <div className="h-6 w-6 mt-1 rounded-full bg-orange-500/10 flex items-center justify-center border border-orange-500/30 text-xs text-orange-400 flex-shrink-0">
                                ✓
                            </div>
                            <p><Highlight>Commitment to Quality:</Highlight> We understand that viewers deserve the best possible experience. That's why we prioritize high-definition content, ensuring crystal-clear visuals and immersive audio across our entire library.</p>
                        </li>
                        <li className="flex items-start gap-3">
                            <div className="h-6 w-6 mt-1 rounded-full bg-orange-500/10 flex items-center justify-center border border-orange-500/30 text-xs text-orange-400 flex-shrink-0">
                                ✓
                            </div>
                            <p><Highlight>Vast & Diverse Library:</Highlight> Our carefully curated selection spans multiple genres and languages, ensuring there's something for everyone. From action-packed blockbusters to thought-provoking documentaries, {SITE_NAME} has you covered.</p>
                        </li>
                        <li className="flex items-start gap-3">
                            <div className="h-6 w-6 mt-1 rounded-full bg-orange-500/10 flex items-center justify-center border border-orange-500/30 text-xs text-orange-400 flex-shrink-0">
                                ✓
                            </div>
                            <p><Highlight>Daily Updates:</Highlight> The world of entertainment is always evolving, and so are we. Our library is updated daily with the latest movies and TV show episodes, so you never miss out on new releases.</p>
                        </li>
                        <li className="flex items-start gap-3">
                            <div className="h-6 w-6 mt-1 rounded-full bg-orange-500/10 flex items-center justify-center border border-orange-500/30 text-xs text-orange-400 flex-shrink-0">
                                ✓
                            </div>
                            <p><Highlight>Community-Focused:</Highlight> We value our users and actively listen to their feedback. Through our <button type="button" onClick={(e) => { e.preventDefault(); redirect("Telegram Channel", TELEGRAM_URL); }} className="text-orange-400 hover:underline font-semibold bg-transparent border-none p-0 cursor-pointer inline align-baseline">Telegram channel</button>, we offer a space for requests, updates, and community interaction, making {SITE_NAME} a platform built for its users.</p>
                        </li>
                    </ul>
                </div>

                <p className="pt-4">Join thousands of satisfied users who have made <Highlight>{SITE_NAME}</Highlight> their go-to entertainment hub. We are dedicated to continuously improving and expanding our service to bring you the best in entertainment.</p>
            </div>
        </>
    );
}
